import React from 'react';

// components
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import styled from 'styled-components';
import classNames from 'classnames';
import Grow from '../../transitions/grow';
import Icon from '../icon';
import { colors } from '../../style';
import { actionColor, inputBorderColor } from '../../style/theme-helpers';

export interface CheckboxProps {
  stopPropagation?: boolean;
  ariaLabel?: string;
  ariaLabelledby?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onChange?: (checked: boolean) => void;
  checked?: boolean | 'indeterminate';
  id?: string;
  disabled?: boolean;
  className?: string;
  'data-testid'?: string;

  required?: boolean;
}

const Root = styled(RadixCheckbox.Root)`
  height: 18px;
  width: 18px;
  background: white;
  border: 2px solid ${inputBorderColor};
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;

  &[data-state='checked'],
  &[data-state='indeterminate'] {
    background: ${props => props.theme.inputBorderColor ?? props.theme.actionColor};
    border-color: ${props => props.theme.inputBorderColor ?? props.theme.actionColor};

    i {
      color: ${colors.white};
    }

    &[disabled] {
      background-color: ${colors.gray400};
      border-color: ${colors.gray400};
    }
  }

  &:not([disabled]) {
    cursor: pointer;
  }

  &:focus-visible {
    outline: 1px solid ${actionColor};
    outline-offset: 1px;
  }

  &[disabled] {
    background-color: ${colors.gray100};
    border-color: ${colors.gray400};
    cursor: not-allowed;
  }
`;

const Indicator = styled(RadixCheckbox.Indicator)`
  display: flex;
  align-items: center;

  i {
    font-weight: 700;
  }
`;

const CheckboxContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &:hover:not(.disabled) {
    .hover-overlay {
      background: ${colors.gray300};
    }
  }

  .hover-overlay {
    width: 30px;
    height: 30px;
    position: absolute;
    z-index: -1;
    border-radius: 50%;
    left: -6px;
    top: -6px;
    transition: 0.2s;
  }
`;

const Checkbox = ({
  onChange,
  checked,
  stopPropagation,
  id,
  disabled,
  className,
  required,
  ariaLabel,
  ariaLabelledby,
  onClick: onClickProp,
  'data-testid': dataTestId,
}: CheckboxProps) => {
  const isChecked = checked;

  const onCheckboxChange = (checked: boolean) => {
    onChange?.(checked);
  };

  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (stopPropagation) {
      e?.stopPropagation?.();
    }

    onClickProp?.(e);
  };

  const isVisible = isChecked === true || isChecked === 'indeterminate';
  const iconName = isChecked === 'indeterminate' ? 'MINUS' : 'DONE';

  return (
    <CheckboxContainer className={classNames({ disabled })}>
      <div className={'hover-overlay'} />
      <Root
        required={required}
        data-testid={dataTestId}
        className={classNames(className, 'checkbox-root')}
        id={id}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        onClick={onClick}
        checked={isChecked}
        disabled={disabled}
        onCheckedChange={onCheckboxChange}
      >
        <Grow isVisible={isVisible}>
          <Indicator>
            <Icon variant={'inherit'} name={iconName} size={'lg'} />
          </Indicator>
        </Grow>
      </Root>
    </CheckboxContainer>
  );
};

export default Checkbox;
